import { useCallback, useEffect, useState } from "react";

/**
 * Клиентский выключатель чисел под ответом (DECISIONS.md п.4).
 *
 * Это НЕ `useDevMode`. Тот открывает прибор — флаги, журнал дат, сырой статус; этот
 * оставляет продуктовую поверхность продуктовой и добавляет к ней числа, за которыми
 * приходит не отладчик пайплайна, а читатель, которому мало одного вердикта.
 *
 * ЗАЩЁЛКА ЖИВЁТ В ВЕРХНЕЙ ПАНЕЛИ, а читают её в консоли (UX_SPEC §4.1) — то есть у режима
 * два держателя в разных поддеревьях. Поэтому состояние вынесено в один модульный источник
 * с подписчиками: два независимых `useState` разъехались бы молча, и защёлка перестала бы
 * управлять тем, что под ней подписано. Хук остаётся хуком — меняется только то, откуда он
 * берёт значение.
 *
 * Горячей клавиши нет сознательно: `Ctrl+Alt+D` — аварийный вход разработчика, а этот режим
 * включают кнопкой, которую видно. Связь односторонняя: `devMode` включает показ чисел,
 * `advanced` служебную панель НЕ открывает.
 *
 * Хранится в `localStorage` по той же причине, что и служебный режим: тот, кто включил
 * числа один раз, хочет их и на следующем поле, и завтра.
 */
const STORAGE_KEY = "kopateli.advanced";

const readStored = (): boolean => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "on";
  } catch {
    return false; // private mode / blocked storage: просто не запоминаем
  }
};

let current: boolean | null = null;
const listeners = new Set<(value: boolean) => void>();

const read = (): boolean => {
  if (current === null) current = readStored();
  return current;
};

const write = (next: boolean): void => {
  current = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, next ? "on" : "off");
  } catch {
    // Не фатально: режим всё равно работает в этой сессии.
  }
  for (const listener of listeners) listener(next);
};

/** Тесты стартуют с чистого `localStorage`; без этого модульный кэш пережил бы `clear()`. */
export const resetAdvancedForTests = (): void => {
  current = null;
  for (const listener of listeners) listener(read());
};

/**
 * Включить режим из теста, который рисует ОДНУ консоль. Защёлка живёт в верхней панели, а
 * не в колонке, и без неё дотянуться до режима из теста консоли можно было бы только
 * подделкой `localStorage` — то есть в обход того самого источника, который проверяется.
 */
export const setAdvancedForTests = (value: boolean): void => write(value);

export const useAdvanced = (): { advanced: boolean; toggleAdvanced: () => void } => {
  const [advanced, setAdvanced] = useState<boolean>(read);

  useEffect(() => {
    listeners.add(setAdvanced);
    setAdvanced(read());
    return () => {
      listeners.delete(setAdvanced);
    };
  }, []);

  const toggleAdvanced = useCallback(() => write(!read()), []);

  return { advanced, toggleAdvanced };
};
